/* Phase 3 — Provider dashboard. Role-restricted: provider users see only
   referrals targeted at their bound organization. Accept / decline with
   structured reasons / request clarification, submit connection evidence,
   and publish capacity. */
import { useState, useEffect, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldAlert, X, CheckCircle2, XCircle, HelpCircle, FileCheck2, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { useUserAuth } from "@/context/UserAuthContext";
import { StateChip, isOverdue } from "@/pages/GuideWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md text-navy focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 text-sm";
const label = "block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5";
const btnGold = "inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-4 py-2.5 rounded-md text-sm disabled:opacity-50 transition-colors";
const btnGhost = "inline-flex items-center justify-center gap-2 border border-navy/25 hover:border-navy text-navy font-semibold px-3 py-2 rounded-md text-xs transition-colors";
const chip = "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-bold border";

const DECLINE_REASONS = [
  ["no_capacity", "No capacity right now"],
  ["ineligible_population", "Person not eligible for our program"],
  ["outside_geography", "Outside our service area"],
  ["duplicate_referral", "Duplicate / already serving"],
  ["other", "Other (explain)"],
];

export default function ProviderDashboard() {
  const { user, loading, authHeaders } = useUserAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [responding, setResponding] = useState(null); // case id
  const [evidencing, setEvidencing] = useState(null);
  const [capacityEdit, setCapacityEdit] = useState(false);

  const isProvider = user && user.roles.includes("provider_user");

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/coordination/provider/cases`, { headers: authHeaders() });
    setData(r.data);
  }, [authHeaders]);

  useEffect(() => { if (isProvider) load().catch((e) => setErr(e?.response?.data?.detail || "Could not load referrals.")); }, [isProvider, load]);

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold-deep" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: "/provider" }} replace />;
  if (!isProvider) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-borderSubtle rounded-xl p-8 text-center" data-testid="provider-forbidden">
          <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
          <h1 className="mt-4 font-display font-black text-navy text-2xl">Provider dashboard</h1>
          <p className="mt-2 text-slateSoft text-sm">This area requires a provider account bound to a partner organization.</p>
          <Link to="/" className={`${btnGhost} mt-6 inline-flex`}>Back to home</Link>
        </div>
      </div>
    );
  }

  const org = data?.organization;

  return (
    <div className="min-h-screen bg-cream" data-testid="provider-dashboard">
      <Seo title="Provider Dashboard · WHO" path="/provider" description="Provider referral dashboard" />
      <PilotDemoBanner text="Pilot dashboard — referrals shown here are synthetic demonstration records." />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-gold-deep">Provider dashboard</p>
            <h1 className="mt-2 font-display font-black text-navy text-3xl">{org?.display_name || "Your organization"}</h1>
            <p className="mt-1 text-sm text-slateSoft">Review warm handoffs, respond, and confirm real connections. You only see cases addressed to your organization.</p>
          </div>
          <button className={btnGhost} onClick={() => setCapacityEdit(true)} data-testid="capacity-edit-btn">
            Capacity: <span className="capitalize font-bold">{org?.capacity_status || "not set"}</span>
          </button>
        </div>
        {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="provider-error">{err}</p>}

        <div className="mt-8 space-y-4" data-testid="provider-case-list">
          {!data && <Loader2 className="w-5 h-5 animate-spin text-gold-deep" />}
          {data?.cases?.length === 0 && (
            <p className="text-sm text-slateSoft bg-white border border-borderSubtle rounded-lg p-8 text-center">No referrals addressed to your organization yet.</p>
          )}
          {data?.cases?.map(({ case: c, evidence, veteran }) => (
            <div key={c.id} className="bg-white border border-borderSubtle rounded-lg p-5" data-testid={`provider-case-${c.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <StateChip value={c.state} />
                <span className="flex items-center gap-1.5">
                  {isOverdue(c) && <span className={`${chip} bg-amber-50 text-amber-800 border-amber-200`}><Clock className="w-3 h-3" /> overdue</span>}
                </span>
              </div>
              <p className="mt-2 font-semibold text-navy">{c.need_summary}</p>
              <p className="mt-1 text-xs text-slateSoft">
                {c.need_category.replace(/_/g, " ")} · Veteran: {veteran?.display_name || "shared after acceptance"}
                {c.next_action_due_at && ` · due ${new Date(c.next_action_due_at).toLocaleString()}`}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {["provider_response", "bounded_recovery"].includes(c.state) && (
                  <>
                    <button className={btnGold} onClick={() => setResponding({ id: c.id, kind: "accepted" })} data-testid={`respond-accept-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Accept</button>
                    <button className={btnGhost} onClick={() => setResponding({ id: c.id, kind: "declined" })} data-testid={`respond-decline-${c.id}`}><XCircle className="w-3.5 h-3.5" /> Decline</button>
                    <button className={btnGhost} onClick={() => setResponding({ id: c.id, kind: "clarification_requested" })} data-testid={`respond-clarify-${c.id}`}><HelpCircle className="w-3.5 h-3.5" /> Ask a question</button>
                  </>
                )}
                {["confirming_connection", "service_outcome", "bounded_recovery"].includes(c.state) && (
                  <button className={btnGold} onClick={() => setEvidencing(c.id)} data-testid={`evidence-add-${c.id}`}><FileCheck2 className="w-3.5 h-3.5" /> Report connection</button>
                )}
              </div>

              {evidence?.length > 0 && (
                <div className="mt-3 border-t border-borderSubtle pt-3 space-y-1.5">
                  {evidence.map((e) => (
                    <p key={e.id} className="text-xs text-slateSoft" data-testid={`provider-evidence-${e.id}`}>
                      <span className="font-semibold text-navy">{e.claim.replace(/_/g, " ")}</span> · {e.source.replace(/_/g, " ")} · {e.status} · {new Date(e.submitted_at).toLocaleString()}
                      {e.statement ? ` — ${e.statement}` : ""}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {responding && (
        <RespondDialog
          info={responding}
          onClose={() => setResponding(null)}
          onDone={() => { setResponding(null); load(); }}
          authHeaders={authHeaders}
        />
      )}
      {evidencing && (
        <EvidenceDialog caseId={evidencing} onClose={() => setEvidencing(null)} onDone={() => { setEvidencing(null); load(); }} authHeaders={authHeaders} />
      )}
      {capacityEdit && (
        <CapacityDialog org={org} onClose={() => setCapacityEdit(false)} onDone={() => { setCapacityEdit(false); load(); }} authHeaders={authHeaders} />
      )}
    </div>
  );
}

function RespondDialog({ info, onClose, onDone, authHeaders }) {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [err, setErr] = useState("");
  const isDecline = info.kind === "declined";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.post(`${API}/coordination/provider/cases/${info.id}/respond`, {
        response: info.kind,
        decline_reason: isDecline ? reason : undefined,
        detail: detail || undefined,
      }, { headers: authHeaders() });
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Response failed.");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="respond-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-display font-black text-navy text-lg capitalize">{info.kind.replace(/_/g, " ")}</h3>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {info.kind === "accepted" && (
          <p className="text-xs text-navy/70 bg-navy/5 border border-navy/15 rounded-md px-3 py-2">
            Accepting starts the connection window — it is <strong>not</strong> a confirmed outcome. WHO will confirm the connection with the veteran and your evidence.
          </p>
        )}
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
        {isDecline && (
          <label className="block"><span className={label}>Reason (required)</span>
            <select className={input} required value={reason} onChange={(e) => setReason(e.target.value)} data-testid="decline-reason">
              <option value="">— Select reason —</option>
              {DECLINE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        )}
        <label className="block"><span className={label}>Note (optional)</span>
          <textarea className={`${input} min-h-[60px]`} value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="respond-detail" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" className={btnGold} data-testid="respond-submit">Submit</button>
        </div>
      </form>
    </div>
  );
}

function EvidenceDialog({ caseId, onClose, onDone, authHeaders }) {
  const [claim, setClaim] = useState("connected");
  const [statement, setStatement] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.post(`${API}/coordination/provider/cases/${caseId}/evidence`, { claim, statement: statement || undefined }, { headers: authHeaders() });
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not submit evidence.");
    }
  };
  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="evidence-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-display font-black text-navy text-lg">Report connection</h3>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
        <label className="block"><span className={label}>What happened?</span>
          <select className={input} value={claim} onChange={(e) => setClaim(e.target.value)} data-testid="evidence-claim">
            <option value="connected">Connected — we reached the veteran</option>
            <option value="service_received">Service delivered</option>
            <option value="partial">Partial contact</option>
            <option value="not_connected">Unable to reach</option>
          </select>
        </label>
        <label className="block"><span className={label}>Details</span>
          <textarea className={`${input} min-h-[70px]`} value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="e.g. Spoke with the veteran on Tuesday; intake scheduled for Friday." data-testid="evidence-statement" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" className={btnGold} data-testid="evidence-submit">Submit evidence</button>
        </div>
      </form>
    </div>
  );
}

function CapacityDialog({ org, onClose, onDone, authHeaders }) {
  const [status, setStatus] = useState(org?.capacity_status || "accepting");
  const [note, setNote] = useState(org?.capacity_note || "");
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/coordination/provider/capacity`, { capacity_status: status, capacity_note: note }, { headers: authHeaders() });
    onDone();
  };
  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="capacity-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-display font-black text-navy text-lg">Service capacity</h3>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <label className="block"><span className={label}>Current capacity</span>
          <select className={input} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="capacity-status">
            <option value="accepting">Accepting referrals</option>
            <option value="limited">Limited capacity</option>
            <option value="full">Full — please hold referrals</option>
          </select>
        </label>
        <label className="block"><span className={label}>Note for WHO Guides</span>
          <textarea className={`${input} min-h-[60px]`} value={note} onChange={(e) => setNote(e.target.value)} data-testid="capacity-note" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" className={btnGold} data-testid="capacity-submit">Save</button>
        </div>
      </form>
    </div>
  );
}
